import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PersonaDoc } from '@/dao/firestore/PersonasRepository';
import { useTranslation } from 'react-i18next';
import { Users, MapPin, DollarSign, Star } from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer
} from 'recharts';

interface AudienceChartsProps {
  personas: PersonaDoc[];
  showHeader?: boolean;
  showOverview?: boolean;
  mode?: 'modal' | 'slides';
  hoveredFilter?: {type: string, value: string} | null;
  onFilterHover?: (filter: {type: string, value: string} | null) => void;
}

export interface SlideData {
  titleKey: string;
  content: React.ReactNode;
}

const COLORS = [
  'hsl(var(--primary))', 
  'hsl(var(--brand-accent))', 
  'hsl(var(--brand-warm))', 
  'hsl(var(--success))', 
  'hsl(var(--warning))', 
  'hsl(270 85% 65%)', 
  'hsl(330 85% 65%)', 
  'hsl(45 93% 47%)'
];

// Helper function to get audience slides data
export function getAudienceSlides(
  personas: PersonaDoc[], 
  t: any, 
  hoveredFilter?: {type: string, value: string} | null,
  onFilterHover?: (filter: {type: string, value: string} | null) => void
): SlideData[] {
  // Age data processing
  const ageData = personas.reduce((acc, persona) => {
    if (!persona.age) return acc;
    const ageRange = persona.age < 25 ? '18-24' : 
                    persona.age < 35 ? '25-34' :
                    persona.age < 45 ? '35-44' :
                    persona.age < 55 ? '45-54' : '55+';
    acc[ageRange] = (acc[ageRange] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const ageChartData = Object.entries(ageData).map(([range, count]) => ({
    range,
    count,
    percentage: Math.round((count / personas.length) * 100)
  }));

  // Gender data processing
  const genderData = personas.reduce((acc, persona) => {
    if (!persona.gender) return acc;
    acc[persona.gender] = (acc[persona.gender] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const genderChartData = Object.entries(genderData).map(([gender, count]) => ({
    gender,
    count,
    percentage: Math.round((count / personas.length) * 100)
  }));

  // Location data processing
  const locationData = personas.reduce((acc, persona) => {
    if (!persona.location?.city) return acc;
    acc[persona.location.city] = (acc[persona.location.city] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const locationChartData = Object.entries(locationData)
    .sort(([,a], [,b]) => b - a)
    .slice(0, 8)
    .map(([city, count]) => ({
      city,
      count,
      percentage: Math.round((count / personas.length) * 100)
    }));

  // Country data processing
  const countryData = personas.reduce((acc, persona) => {
    if (!persona.location?.country) return acc;
    acc[persona.location.country] = (acc[persona.location.country] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const countryChartData = Object.entries(countryData)
    .sort(([,a], [,b]) => b - a)
    .slice(0, 10)
    .map(([country, count]) => ({
      country,
      count,
      percentage: Math.round((count / personas.length) * 100)
    }));

  // Income data processing
  const incomeData = personas.reduce((acc, persona) => {
    if (!persona.estimated_income_eur || persona.estimated_income_eur === 0) return acc;
    const income = persona.estimated_income_eur;
    const range = income < 30000 ? t('audience.incomeRanges.low') :
                 income < 50000 ? t('audience.incomeRanges.medium') :
                 income < 80000 ? t('audience.incomeRanges.high') : t('audience.incomeRanges.veryHigh');
    acc[range] = (acc[range] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const incomeChartData = Object.entries(incomeData)
    .sort(([,a], [,b]) => b - a)
    .map(([range, count]) => ({
      range,
      count,
      percentage: Math.round((count / personas.length) * 100)
    }));

  // Occupation data processing
  const allOccupations = personas.flatMap(persona => {
    if (!persona.occupation || persona.occupation.trim() === '') return [];
    
    return persona.occupation
      .split(/[,/;]/)
      .map(occ => occ.trim())
      .filter(occ => occ.length > 0);
  });

  const occupationData = allOccupations.reduce((acc, occupation) => {
    acc[occupation] = (acc[occupation] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const occupationChartData = Object.entries(occupationData)
    .sort(([,a], [,b]) => b - a)
    .slice(0, 8)
    .map(([occupation, count]) => ({
      occupation: occupation.length > 20 ? occupation.substring(0, 20) + '...' : occupation,
      fullOccupation: occupation,
      count,
      percentage: Math.round((count / allOccupations.length) * 100)
    }));

  // Tech proficiency data processing
  const techProficiencyData = personas.reduce((acc, persona) => {
    if (!persona.technological_proficiency) return acc;
    const firstWord = persona.technological_proficiency.split('(')[0].toLowerCase().trim();
    acc[firstWord] = (acc[firstWord] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const techProficiencyChartData = Object.entries(techProficiencyData).map(([level, count]) => ({
    level,
    count,
    percentage: Math.round((count / personas.length) * 100)
  }));

  // Platform data processing
  const platformUsage = personas.reduce((acc, persona) => {
    const platforms = persona.preferred_platforms || [];
    platforms.forEach(platform => {
      if (platform && platform.trim()) {
        const cleanPlatform = platform.trim();
        if (!acc[cleanPlatform]) {
          acc[cleanPlatform] = new Set();
        }
        acc[cleanPlatform].add(persona.id);
      }
    });
    return acc;
  }, {} as Record<string, Set<string>>);

  const platformsData = Object.entries(platformUsage)
    .map(([platform, userSet]) => ({
      platform,
      users: userSet.size,
      percentage: Math.round((userSet.size / personas.length) * 100)
    }))
    .sort((a, b) => b.users - a.users)
    .slice(0, 8);

  const slides: SlideData[] = [
    {
      titleKey: 'screeningRoom.slides.demographics',
      content: (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-80">
          <div className="h-full">
            <h4 className="text-sm font-medium mb-3 text-center">{t('screeningRoom.slides.ageDistribution')}</h4>
            <div className="h-[calc(100%-2rem)]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={ageChartData}>
                  <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                  <XAxis dataKey="range" fontSize={12} />
                  <YAxis fontSize={12} />
                  <Tooltip 
                    content={({ active, payload, label }) => {
                      if (active && payload && payload.length) {
                        return (
                          <div className="bg-background border rounded-lg shadow-lg p-3">
                            <p className="font-medium">{label}</p>
                            <p className="text-primary">
                              {t('screeningRoom.tooltips.personas', { count: Number(payload[0].value), percentage: payload[0].payload.percentage })}
                            </p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Bar 
                    dataKey="count" 
                    fill="hsl(var(--primary))" 
                    radius={[4, 4, 0, 0]}
                    onMouseEnter={(data) => onFilterHover?.({type: 'age', value: data.range})}
                    onMouseLeave={() => onFilterHover?.(null)}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="h-full">
            <h4 className="text-sm font-medium mb-3 text-center">{t('screeningRoom.slides.genderDistribution')}</h4>
            <div className="h-[calc(100%-2rem)]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={genderChartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ gender, percentage }) => `${t(`audience.genders.${gender.toLowerCase()}`)} (${percentage}%)`}
                    outerRadius={60}
                    fill="#8884d8"
                    dataKey="count"
                    onMouseEnter={(data) => onFilterHover?.({type: 'gender', value: data.gender})}
                    onMouseLeave={() => onFilterHover?.(null)}
                  >
                    {genderChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        return (
                          <div className="bg-background border rounded-lg shadow-lg p-3">
                            <p className="font-medium">{t(`audience.genders.${data.gender.toLowerCase()}`)}</p>
                            <p className="text-primary">{t('audience.tooltips.personas', { count: data.count })}</p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )
    },
    {
      titleKey: 'screeningRoom.slides.geography',
      content: (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-80">
          <div className="h-full">
            <h4 className="text-sm font-medium mb-3 text-center">{t('screeningRoom.slides.byCountries')}</h4>
            <div className="h-[calc(100%-2rem)]">
              {countryChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={countryChartData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ country, percentage }) => `${country} (${percentage}%)`}
                      outerRadius={60}
                      fill="#8884d8"
                      dataKey="count"
                      onMouseEnter={(data) => onFilterHover?.({type: 'country', value: data.country})}
                      onMouseLeave={() => onFilterHover?.(null)}
                    >
                      {countryChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          return (
                            <div className="bg-background border rounded-lg shadow-lg p-3">
                              <p className="font-medium">{data.country}</p>
                              <p className="text-primary">{t('audience.tooltips.personas', { count: data.count })}</p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-muted-foreground">
                  {t('audience.noData.countries')}
                </div>
              )}
            </div>
          </div>
          <div className="h-full">
            <h4 className="text-sm font-medium mb-3 text-center">{t('screeningRoom.slides.byCities')}</h4>
            <div className="h-[calc(100%-2rem)]">
              {locationChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={locationChartData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ city, percentage }) => `${city} (${percentage}%)`}
                      outerRadius={60}
                      fill="#8884d8"
                      dataKey="count"
                      onMouseEnter={(data) => onFilterHover?.({type: 'city', value: data.city})}
                      onMouseLeave={() => onFilterHover?.(null)}
                    >
                      {locationChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          return (
                            <div className="bg-background border rounded-lg shadow-lg p-3">
                              <p className="font-medium">{data.city}</p>
                              <p className="text-primary">{t('audience.tooltips.personas', { count: data.count })}</p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-muted-foreground">
                  {t('audience.noData.cities')}
                </div>
              )}
            </div>
          </div>
        </div>
      )
    },
    {
      titleKey: 'screeningRoom.slides.economic',
      content: (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-80">
          <div className="h-full">
            <h4 className="text-sm font-medium mb-3 text-center">{t('screeningRoom.slides.incomeDistribution')}</h4>
            <div className="h-[calc(100%-2rem)]">
              {incomeChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={incomeChartData}>
                    <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                    <XAxis dataKey="range" fontSize={10} angle={-45} textAnchor="end" height={80} />
                    <YAxis fontSize={12} />
                    <Tooltip 
                      content={({ active, payload, label }) => {
                        if (active && payload && payload.length) {
                          return (
                            <div className="bg-background border rounded-lg shadow-lg p-3">
                              <p className="font-medium">{label}</p>
                              <p className="text-primary">
                                {t('screeningRoom.tooltips.personas', { count: Number(payload[0].value), percentage: payload[0].payload.percentage })}
                              </p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Bar 
                      dataKey="count" 
                      fill="hsl(var(--brand-warm))" 
                      radius={[4, 4, 0, 0]}
                      onMouseEnter={(data) => onFilterHover?.({type: 'income', value: data.range})}
                      onMouseLeave={() => onFilterHover?.(null)}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-muted-foreground">
                  {t('audience.noData.income')}
                </div>
              )}
            </div>
          </div>
          <div className="h-full">
            <h4 className="text-sm font-medium mb-3 text-center">{t('screeningRoom.slides.occupationDistribution')}</h4>
            <div className="h-[calc(100%-2rem)]">
              {occupationChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={occupationChartData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ occupation, percentage }) => `${occupation} (${percentage}%)`}
                      outerRadius={60}
                      fill="#8884d8"
                      dataKey="count"
                      onMouseEnter={(data) => onFilterHover?.({type: 'occupation', value: data.fullOccupation})}
                      onMouseLeave={() => onFilterHover?.(null)}
                    >
                      {occupationChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          return (
                            <div className="bg-background border rounded-lg shadow-lg p-3">
                              <p className="font-medium">{data.fullOccupation}</p>
                              <p className="text-primary">
                                {t('audience.tooltips.mentions', { count: data.count, percentage: data.percentage })}
                              </p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-muted-foreground">
                  {t('audience.noData.occupations')}
                </div>
              )}
            </div>
          </div>
        </div>
      )
    },
    {
      titleKey: 'screeningRoom.slides.technology',
      content: (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-80">
          <div className="h-full">
            <h4 className="text-sm font-medium mb-3 text-center">{t('screeningRoom.slides.techProficiency')}</h4>
            <div className="h-[calc(100%-2rem)]">
              {techProficiencyChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={techProficiencyChartData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ level, percentage }) => `${level} (${percentage}%)`}
                      outerRadius={60}
                      fill="#8884d8"
                      dataKey="count"
                      onMouseEnter={(data) => onFilterHover?.({type: 'tech', value: data.level})}
                      onMouseLeave={() => onFilterHover?.(null)}
                    >
                      {techProficiencyChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          return (
                            <div className="bg-background border rounded-lg shadow-lg p-3">
                              <p className="font-medium">{data.level}</p>
                              <p className="text-primary">{t('audience.tooltips.personas', { count: data.count })}</p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-muted-foreground">
                  {t('audience.noData.tech')}
                </div>
              )}
            </div>
          </div>
          <div className="h-full">
            <h4 className="text-sm font-medium mb-3 text-center">{t('screeningRoom.slides.platformPreferences')}</h4>
            <div className="h-[calc(100%-2rem)]">
              {platformsData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={platformsData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ platform, percentage }) => `${platform} (${percentage}%)`}
                      outerRadius={60}
                      fill="#8884d8"
                      dataKey="users"
                      onMouseEnter={(data) => onFilterHover?.({type: 'platform', value: data.platform})}
                      onMouseLeave={() => onFilterHover?.(null)}
                    >
                      {platformsData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          return (
                            <div className="bg-background border rounded-lg shadow-lg p-3">
                              <p className="font-medium">{data.platform}</p>
                              <p className="text-primary">
                                {t('audience.tooltips.users', { count: data.users, percentage: data.percentage })}
                              </p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-muted-foreground">
                  {t('audience.noData.platforms')}
                </div>
              )}
            </div>
          </div>
        </div>
      )
    }
  ].filter(slide => {
    // Only show slides that have data
    switch (slide.titleKey) {
      case 'screeningRoom.slides.demographics':
        return ageChartData.length > 0 || genderChartData.length > 0;
      case 'screeningRoom.slides.geography':
        return locationChartData.length > 0 || countryChartData.length > 0;
      case 'screeningRoom.slides.economic':
        return incomeChartData.length > 0 || occupationChartData.length > 0;
      case 'screeningRoom.slides.technology':
        return platformsData.length > 0 || techProficiencyChartData.length > 0;
      default:
        return true;
    }
  });

  return slides;
}

export function AudienceCharts({ 
  personas, 
  showHeader = true, 
  showOverview = true,
  mode = 'modal',
  hoveredFilter,
  onFilterHover
}: AudienceChartsProps): JSX.Element {
  const { t } = useTranslation();

  // Age data processing
  const ageData = personas.reduce((acc, persona) => {
    if (!persona.age) return acc;
    const ageRange = persona.age < 25 ? '18-24' : 
                    persona.age < 35 ? '25-34' :
                    persona.age < 45 ? '35-44' :
                    persona.age < 55 ? '45-54' : '55+';
    acc[ageRange] = (acc[ageRange] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const ageChartData = Object.entries(ageData).map(([range, count]) => ({
    range,
    count,
    percentage: Math.round((count / personas.length) * 100)
  }));

  // Gender data processing
  const genderData = personas.reduce((acc, persona) => {
    if (!persona.gender) return acc;
    acc[persona.gender] = (acc[persona.gender] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const genderChartData = Object.entries(genderData).map(([gender, count]) => ({
    gender,
    count,
    percentage: Math.round((count / personas.length) * 100)
  }));

  // Country data processing
  const countryData = personas.reduce((acc, persona) => {
    if (!persona.location?.country) return acc;
    acc[persona.location.country] = (acc[persona.location.country] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const countryChartData = Object.entries(countryData)
    .sort(([,a], [,b]) => b - a)
    .slice(0, 5)
    .map(([country, count]) => ({
      country,
      count,
      percentage: Math.round((count / personas.length) * 100)
    }));

  // Income data processing
  const incomeData = personas.reduce((acc, persona) => {
    if (!persona.estimated_income_eur || persona.estimated_income_eur === 0) return acc;
    const income = persona.estimated_income_eur;
    const range = income < 30000 ? t('audience.incomeRanges.low') :
                 income < 50000 ? t('audience.incomeRanges.medium') :
                 income < 80000 ? t('audience.incomeRanges.high') : t('audience.incomeRanges.veryHigh');
    acc[range] = (acc[range] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const incomeChartData = Object.entries(incomeData)
    .sort(([,a], [,b]) => b - a)
    .map(([range, count]) => ({
      range,
      count,
      percentage: Math.round((count / personas.length) * 100)
    }));

  if (personas.length === 0) {
    return (
      <Card>
        <CardContent className="text-center py-8 text-muted-foreground">
          {t('audience.noData.audience')}
        </CardContent>
      </Card>
    );
  }

  // Modal mode - renders as cards
  return (
    <section>
      {showHeader && (
        <div className="flex items-center gap-3 mb-4">
          <Users className="h-6 w-6 text-primary" />
          <h2 className="text-xl font-semibold">{t('audience.overview.title')}</h2>
        </div>
      )}

      {showOverview && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">{t('audience.overview.personas')}</span>
              </div>
              <div className="text-2xl font-bold">{personas.length}</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Star className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">{t('audience.overview.avgAge')}</span>
              </div>
              <div className="text-2xl font-bold">
                {Math.round(personas.reduce((sum, p) => sum + (p.age || 0), 0) / personas.length)} {t('audience.overview.years')}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">{t('audience.overview.countries')}</span>
              </div>
              <div className="text-2xl font-bold">
                {new Set(personas.map(p => p.location?.country).filter(Boolean)).size}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">{t('audience.overview.avgIncome')}</span>
              </div>
              <div className="text-2xl font-bold">
                {Math.round(personas.reduce((sum, p) => sum + (p.estimated_income_eur || 0), 0) / personas.length / 1000)}k€
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Age Distribution */}
        {ageChartData.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>{t('screeningRoom.slides.ageDistributionTitle')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[200px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={ageChartData}>
                    <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                    <XAxis dataKey="range" fontSize={12} />
                    <YAxis fontSize={12} />
                    <Tooltip 
                      content={({ active, payload, label }) => {
                        if (active && payload && payload.length) {
                          return (
                            <div className="bg-background border rounded-lg shadow-lg p-3">
                              <p className="font-medium">{label}</p>
                              <p className="text-primary">
                                {t('audience.tooltips.personasPercentage', { count: payload[0].value, percentage: payload[0].payload.percentage })}
                              </p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Bar dataKey="count" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Gender Distribution */}
        {genderChartData.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>{t('screeningRoom.slides.genderDistributionTitle')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[200px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={genderChartData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ gender, percentage }) => `${t(`audience.genders.${gender.toLowerCase()}`)} (${percentage}%)`}
                      outerRadius={60}
                      fill="#8884d8"
                      dataKey="count"
                    >
                      {genderChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Countries */}
        {countryChartData.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>{t('screeningRoom.slides.mainCountriesTitle')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[200px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={countryChartData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ country, percentage }) => `${country} (${percentage}%)`}
                      outerRadius={60}
                      fill="#8884d8"
                      dataKey="count"
                    >
                      {countryChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Income Distribution */}
        {incomeChartData.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>{t('audience.charts.incomeDistribution')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[200px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={incomeChartData}>
                    <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                    <XAxis dataKey="range" fontSize={11} angle={-45} textAnchor="end" height={60} />
                    <YAxis fontSize={12} />
                    <Tooltip />
                    <Bar dataKey="count" fill="hsl(var(--brand-warm))" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </section>
  );
}